import React, { useMemo, useState, useCallback } from 'react';
import { TabState } from '../../../../types';
import { useProjectData } from '../../../stores/hooks/useProjectData';
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { isDatasetCompatible, predictGLM } from '../../../utils/glm-prediction';
import { DatasetAccessor } from '../../../utils/DatasetAccessor';
import { hasData } from '../../../../types/dataset.types';
import { createColumnName } from '../../../../types';
import { TabRegistry } from '../../../tab-system/TabRegistry';
import logger from '../../../utils/logger';
import * as styles from './GLMPredictionTabContent.css';

interface GLMPredictionTabContentProps {
  tab: TabState;
}

export const GLMPredictionTabContent: React.FC<GLMPredictionTabContentProps> = ({ tab }) => {
  const { project, updateProject, getAllDatasets } = useProjectData();
  const { addTab, getActiveContainer } = usePaneLayout();

  // タブのメタデータからモデルIDを取得
  const modelId = tab.metadata?.modelId;

  // 状態管理
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | undefined>();
  const [outputName, setOutputName] = useState('');
  const [includeOriginalData, setIncludeOriginalData] = useState(true);
  const [confidenceLevels, setConfidenceLevels] = useState<number[]>([95]);
  const [predictionLevels, setPredictionLevels] = useState<number[]>([95]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [resultDatasetId, setResultDatasetId] = useState<string | undefined>();
  const [accuracyMetrics, setAccuracyMetrics] = useState<{
    r2: number;
    rmse: number;
    mae: number;
  } | undefined>();

  // モデルを取得
  const model = useMemo(() => {
    if (!project || !modelId) {
      logger.warn('[GLMPrediction] Cannot get model:', {
        hasProject: !!project,
        modelId
      });
      return null;
    }

    const foundModel = project.models?.[modelId];
    if (!foundModel) {
      logger.error('[GLMPrediction] Model not found:', { modelId });
      return null;
    }

    if (foundModel.type !== 'glm') {
      logger.error('[GLMPrediction] Model is not a GLM model:', { modelId, type: foundModel.type });
      return null;
    }

    return foundModel;
  }, [project, modelId]);

  // トレーニングデータセットを取得
  const trainingDataset = useMemo(() => {
    if (!model) return null;

    const allDatasets = getAllDatasets();
    return allDatasets.find(ds => ds.id === model.metadata.trainingDatasetId) || null;
  }, [model, getAllDatasets]);

  // 互換性のあるデータセットのリストを生成
  const compatibleDatasets = useMemo(() => {
    if (!model || !trainingDataset) return [];

    const allDatasets = getAllDatasets();
    return allDatasets.filter(ds => isDatasetCompatible(model, ds, trainingDataset));
  }, [model, trainingDataset, getAllDatasets]);

  // デフォルトの出力名を更新
  const defaultOutputName = useMemo(() => {
    if (!model || !selectedDatasetId) return '';

    const dataset = getAllDatasets().find(ds => ds.id === selectedDatasetId);
    if (!dataset) return '';

    return `GLM Predictions: ${model.name} on ${dataset.name}`;
  }, [model, selectedDatasetId, getAllDatasets]);

  // データセット選択時に出力名を更新
  React.useEffect(() => {
    if (defaultOutputName && !outputName) {
      setOutputName(defaultOutputName);
    }
  }, [defaultOutputName, outputName]);

  // 予測を実行
  const handleRunPrediction = useCallback(async () => {
    if (!model || !selectedDatasetId || !project || !trainingDataset) {
      logger.error('[GLMPrediction] Missing required data for prediction');
      return;
    }

    setIsRunning(true);
    setError(undefined);
    setAccuracyMetrics(undefined);

    try {
      const dataset = getAllDatasets().find(ds => ds.id === selectedDatasetId);
      if (!dataset) {
        throw new Error('Selected dataset not found');
      }

      logger.info('[GLMPrediction] Running prediction:', {
        modelId: model.id,
        datasetId: dataset.id,
        includeOriginalData
      });

      // 予測を実行
      const predictionDataset = predictGLM(model, dataset, trainingDataset, {
        includeOriginalData,
        confidenceLevels,
        predictionLevels
      });

      // 出力名を適用
      predictionDataset.name = outputName || defaultOutputName;

      // プロジェクトに追加
      updateProject((prevProject) => ({
        ...prevProject,
        datasets: {
          ...prevProject.datasets,
          [predictionDataset.id]: predictionDataset
        }
      }));
      setResultDatasetId(predictionDataset.id);

      // 目的変数が含まれている場合、精度指標を計算
      const targetColumnName = trainingDataset.schema.columns.find(
        col => col.id === model.metadata.target
      )?.name;

      if (targetColumnName && dataset.schema.columns.some(col => col.name === targetColumnName) &&
          hasData(dataset) && hasData(predictionDataset)) {
        logger.info('[GLMPrediction] Target variable found, calculating accuracy metrics');

        // DatasetAccessorを使用してデータを取得
        const datasetAccessor = new DatasetAccessor(dataset);
        const predictionAccessor = new DatasetAccessor(predictionDataset);

        const actualValues: number[] = [];
        const predictedValues: number[] = [];

        for (let i = 0; i < (dataset.rowCount ?? 0); i++) {
          const actual = datasetAccessor.getValue(i, createColumnName(targetColumnName));
          const predicted = predictionAccessor.getValue(i, createColumnName('glm_predicted'));

          if (actual !== null && actual !== undefined &&
              predicted !== null && predicted !== undefined) {
            actualValues.push(actual as number);
            predictedValues.push(predicted as number);
          }
        }

        if (actualValues.length > 0) {
          // R²を計算
          const meanActual = actualValues.reduce((sum, val) => sum + val, 0) / actualValues.length;
          const ssTot = actualValues.reduce((sum, val) => sum + Math.pow(val - meanActual, 2), 0);
          const ssRes = actualValues.reduce((sum, val, i) =>
            sum + Math.pow(val - predictedValues[i], 2), 0
          );
          const r2 = 1 - (ssRes / ssTot);

          // RMSEを計算
          const mse = ssRes / actualValues.length;
          const rmse = Math.sqrt(mse);

          // MAEを計算
          const mae = actualValues.reduce((sum, val, i) =>
            sum + Math.abs(val - predictedValues[i]), 0
          ) / actualValues.length;

          setAccuracyMetrics({ r2, rmse, mae });

          logger.info('[GLMPrediction] Accuracy metrics calculated:', { r2, rmse, mae });
        }
      }

      logger.info('[GLMPrediction] Prediction completed:', {
        resultDatasetId: predictionDataset.id,
        rowCount: predictionDataset.rowCount
      });
    } catch (err) {
      logger.error('[GLMPrediction] Prediction failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsRunning(false);
    }
  }, [model, selectedDatasetId, project, trainingDataset, outputName, defaultOutputName, includeOriginalData, confidenceLevels, predictionLevels, updateProject, getAllDatasets]);

  // 結果データセットを開く
  const handleOpenResult = useCallback(() => {
    if (!resultDatasetId) return;

    const dataset = getAllDatasets().find(ds => ds.id === resultDatasetId);
    if (!dataset) {
      logger.error('[GLMPrediction] Result dataset not found:', { resultDatasetId });
      return;
    }

    const dataTableTab = TabRegistry['data-table'].createTab(
      resultDatasetId,
      dataset.name
    );

    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, dataTableTab, { activatePane: true });
    }
  }, [resultDatasetId, getAllDatasets, addTab, getActiveContainer]);

  // 信頼水準選択のハンドラー
  const handleConfidenceLevelChange = useCallback((level: number, checked: boolean) => {
    setConfidenceLevels(prev => {
      if (checked) {
        return [...prev, level].sort((a, b) => a - b);
      } else {
        const filtered = prev.filter(l => l !== level);
        // 少なくとも1つは選択されている必要がある
        return filtered.length > 0 ? filtered : prev;
      }
    });
  }, []);

  const handlePredictionLevelChange = useCallback((level: number, checked: boolean) => {
    setPredictionLevels(prev => {
      if (checked) {
        return [...prev, level].sort((a, b) => a - b);
      } else {
        const filtered = prev.filter(l => l !== level);
        // 少なくとも1つは選択されている必要がある
        return filtered.length > 0 ? filtered : prev;
      }
    });
  }, []);

  if (!model) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyMessage}>
          Model not found
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Prediction: {model.name}</h2>
      </div>

      {/* データセット選択セクション */}
      <section className={styles.section}>
        <h3>Select Dataset for Prediction</h3>
        <p className={styles.helpText}>
          Only datasets with matching feature columns are shown.
        </p>

        {compatibleDatasets.length === 0 ? (
          <div className={styles.warning}>
            No compatible datasets found. The dataset must have the same feature columns as the training data.
          </div>
        ) : (
          <select
            className={styles.select}
            data-testid="prediction-dataset-select"
            value={selectedDatasetId || ''}
            onChange={(e) => setSelectedDatasetId(e.target.value)}
          >
            <option value="">-- Select a dataset --</option>
            {compatibleDatasets.map(ds => (
              <option key={ds.id} value={ds.id}>
                {ds.name}
              </option>
            ))}
          </select>
        )}
      </section>

      {/* 結果データセット設定セクション */}
      <section className={styles.section}>
        <h3>Output Dataset Settings</h3>

        <div className={styles.formGroup}>
          <label htmlFor="output-name">Output Dataset Name</label>
          <input
            id="output-name"
            type="text"
            className={styles.input}
            value={outputName}
            onChange={(e) => setOutputName(e.target.value)}
            placeholder={defaultOutputName}
          />
        </div>

        <div className={styles.formGroup}>
          <label>
            <input
              type="checkbox"
              checked={includeOriginalData}
              onChange={(e) => setIncludeOriginalData(e.target.checked)}
            />
            {' '}Include original data
          </label>
        </div>
      </section>

      {/* 信頼水準設定セクション */}
      <section className={styles.section}>
        <h3>Confidence and Prediction Levels</h3>
        <p className={styles.helpText}>
          Select the confidence levels for intervals. Multiple levels can be selected.
        </p>

        <div className={styles.formGroup}>
          <label>Confidence Interval Levels</label>
          <div className={styles.checkboxGroup}>
            {[90, 95, 99].map(level => (
              <label key={`ci-${level}`} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={confidenceLevels.includes(level)}
                  onChange={(e) => handleConfidenceLevelChange(level, e.target.checked)}
                />
                {level}%
              </label>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Prediction Interval Levels</label>
          <div className={styles.checkboxGroup}>
            {[90, 95, 99].map(level => (
              <label key={`pi-${level}`} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={predictionLevels.includes(level)}
                  onChange={(e) => handlePredictionLevelChange(level, e.target.checked)}
                />
                {level}%
              </label>
            ))}
          </div>
        </div>
      </section>

      {/* 実行ボタン */}
      <section className={styles.actionSection}>
        <button
          className={styles.primaryButton}
          onClick={handleRunPrediction}
          disabled={!selectedDatasetId || isRunning}
        >
          {isRunning ? 'Running...' : 'Run Prediction'}
        </button>
      </section>

      {/* エラー表示 */}
      {error && (
        <section className={styles.section}>
          <div className={styles.error}>
            Error: {error}
          </div>
        </section>
      )}

      {/* 結果表示 */}
      {resultDatasetId && !error && (
        <section className={styles.section}>
          <div className={styles.success}>
            Prediction completed successfully!
          </div>

          {accuracyMetrics && (
            <div className={styles.metricsContainer}>
              <h4 className={styles.metricsTitle}>Prediction Accuracy Metrics</h4>
              <p className={styles.metricsDescription}>
                These metrics compare predicted values with actual values in the dataset.
              </p>
              <div className={styles.metricsGrid}>
                <div className={styles.metricItem}>
                  <div className={styles.metricLabel}>R² (Coefficient of Determination)</div>
                  <div className={styles.metricValue}>{accuracyMetrics.r2.toFixed(4)}</div>
                  <div className={styles.metricHelp}>
                    Proportion of variance explained (0-1, higher is better)
                  </div>
                </div>
                <div className={styles.metricItem}>
                  <div className={styles.metricLabel}>RMSE (Root Mean Square Error)</div>
                  <div className={styles.metricValue}>{accuracyMetrics.rmse.toFixed(4)}</div>
                  <div className={styles.metricHelp}>
                    Average prediction error (lower is better)
                  </div>
                </div>
                <div className={styles.metricItem}>
                  <div className={styles.metricLabel}>MAE (Mean Absolute Error)</div>
                  <div className={styles.metricValue}>{accuracyMetrics.mae.toFixed(4)}</div>
                  <div className={styles.metricHelp}>
                    Average absolute prediction error (lower is better)
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            className={styles.secondaryButton}
            onClick={handleOpenResult}
          >
            Open Result Dataset
          </button>
        </section>
      )}
    </div>
  );
};
